// =====================================================================
// CertificatsMedicaux.jsx — Day 10 (27-05-2026)
// =====================================================================
// Justificatif médical: liste + 3 statuts (AVEC/SANS/EN_ATTENTE) + upload.
// Accès HR/admin (gate medical_certificates.read în App.jsx).
// Données de santé — RGPD: accès tracé côté backend.
// =====================================================================

import { useState, useEffect, useMemo } from 'react';
import { medicalCerts as api, users as usersApi } from '../services/api.js';

const STATUS_PILL = {
  AVEC:       { background: '#DCFCE7', color: '#166534', border: '1px solid #86EFAC', label: '✓ Avec certificat' },
  SANS:       { background: '#F1F5F9', color: '#475569', border: '1px solid #CBD5E1', label: '— Sans (non requis)' },
  EN_ATTENTE: { background: '#FEF3C7', color: '#78350F', border: '1px solid #F59E0B', label: '⏳ En attente' },
};
const SOURCE_TAG = {
  PRESENCE: { background: '#dbeafe', color: '#1e40af', label: 'Présence' },
  CONGES:   { background: '#ede9fe', color: '#6d28d9', label: 'Congés' },
  MANUAL:   { background: '#f1f5f9', color: '#64748b', label: 'Manuel' },
};

function pill(status) {
  const s = STATUS_PILL[status] || STATUS_PILL.EN_ATTENTE;
  return { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 7, fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', background: s.background, color: s.color, border: s.border };
}
function fmtFR(ymd) {
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
}

export default function CertificatsMedicaux() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);   // cert object or {} for new
  const [allUsers, setAllUsers] = useState([]);

  async function load() {
    setLoading(true); setError(null);
    try {
      const params = {};
      if (statusFilter !== 'ALL') params.status = statusFilter;
      if (search.trim()) params.search = search.trim();
      setRows(await api.list(params));
    } catch (e) {
      setError(e?.response?.data?.message || e.message || 'Erreur de chargement');
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { usersApi.list({ active: 'true' }).then(setAllUsers).catch(() => {}); }, []);

  const summary = useMemo(() => {
    const s = { AVEC: 0, SANS: 0, EN_ATTENTE: 0 };
    for (const r of rows) s[r.status] = (s[r.status] || 0) + 1;
    return s;
  }, [rows]);

  return (
    <div style={{ padding: 24 }}>
      <h1 style={{ fontSize: 22, marginBottom: 2 }}>🩺 Justificatifs médicaux</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginBottom: 16 }}>
        Suivi des certificats pour les absences maladie (MAL). Données de santé — accès réservé RH/admin.
      </p>

      {/* Toolbar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 14 }}>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={selectStyle}>
          <option value="ALL">Tous les statuts</option>
          <option value="AVEC">Avec certificat</option>
          <option value="SANS">Sans (non requis)</option>
          <option value="EN_ATTENTE">En attente ⚠</option>
        </select>
        <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()}
          placeholder="🔍 Rechercher (nom)..." style={{ ...selectStyle, width: 180 }} />
        <button onClick={load} style={btnGhost}>Filtrer</button>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: '#64748b' }}>
          <b style={{ color: summary.EN_ATTENTE > 0 ? '#92400e' : 'inherit' }}>{summary.EN_ATTENTE} en attente</b>
          {' · '}{summary.SANS} sans · {summary.AVEC} avec
        </span>
        <button onClick={() => setEditing({ userId: '', startDate: '', endDate: '', status: 'EN_ATTENTE', source: 'MANUAL', notes: '' })} style={btnPrimary}>
          + Nouveau certificat
        </button>
      </div>

      {error && <div style={alertErr}>{error}</div>}

      {loading ? (
        <div style={{ padding: 24, color: '#64748b' }}>Chargement...</div>
      ) : rows.length === 0 ? (
        <div style={{ padding: 24, background: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: 8, textAlign: 'center', color: '#64748b' }}>
          Aucun certificat pour ce filtre.
        </div>
      ) : (
        <div style={{ border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead><tr style={{ background: '#f8fafc' }}>
              <th style={th}>Personne</th><th style={th}>Période</th><th style={{ ...th, textAlign: 'right' }}>Jours</th>
              <th style={th}>Source</th><th style={th}>Statut</th><th style={th}>Documents</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {rows.map((r) => {
                const src = SOURCE_TAG[r.source] || SOURCE_TAG.MANUAL;
                return (
                  <tr key={r.id} style={{ borderTop: '1px solid #f1f5f9', cursor: 'pointer' }} onClick={() => openEdit(r.id)}>
                    <td style={td}><b>{r.user?.name || r.userId}</b><br /><span style={{ color: '#94a3b8', fontSize: 11 }}>{r.user?.role}</span></td>
                    <td style={td}>{fmtFR(r.startDate)} → {fmtFR(r.endDate)}</td>
                    <td style={{ ...td, textAlign: 'right' }}>{r.workdaysCount} j</td>
                    <td style={td}><span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, fontWeight: 600, background: src.background, color: src.color }}>{src.label}</span></td>
                    <td style={td}><span style={pill(r.status)}>{STATUS_PILL[r.status]?.label}</span></td>
                    <td style={td}>{r.fileCount > 0 ? `📎 ${r.fileCount}` : <span style={{ color: '#cbd5e1' }}>—</span>}</td>
                    <td style={{ ...td, textAlign: 'right' }}><span style={{ color: '#4f46e5', fontWeight: 600, fontSize: 12 }}>Détail →</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <CertModal
          initial={editing}
          allUsers={allUsers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          setEditing={setEditing}
        />
      )}
    </div>
  );

  async function openEdit(id) {
    try { setEditing(await api.get(id)); }
    catch (e) { setError(e?.response?.data?.message || e.message); }
  }
}

// =====================================================================
// Modal create/edit + upload
// =====================================================================
function CertModal({ initial, allUsers, onClose, onSaved, setEditing }) {
  const isNew = !initial.id;
  const [form, setForm] = useState({
    userId: initial.userId || '', startDate: initial.startDate || '', endDate: initial.endDate || '',
    status: initial.status || 'EN_ATTENTE', source: initial.source || 'MANUAL', notes: initial.notes || '',
  });
  const [files, setFiles] = useState(initial.files || []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function save() {
    setErr(null);
    if (!form.userId || !form.startDate || !form.endDate) { setErr('Personne, début et fin requis.'); return; }
    if (form.endDate < form.startDate) { setErr('La date de fin doit être ≥ début.'); return; }
    setBusy(true);
    try {
      if (isNew) {
        const created = await api.create(form);
        // Switch to edit mode of created cert so files can be added
        setEditing({ ...created, files: [], user: allUsers.find((u) => u.id === created.userId) });
      } else {
        await api.update(initial.id, form);
        onSaved();
      }
    } catch (e) { setErr(e?.response?.data?.message || e.message); }
    finally { setBusy(false); }
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr(null);
    try {
      const f = await api.uploadFile(initial.id, file);
      setFiles((prev) => [...prev, f]);
    } catch (e2) { setErr(e2?.response?.data?.message || e2.message); }
    finally { setBusy(false); e.target.value = ''; }
  }

  // 21-10-2026: refuzul serverului (lună închisă…) se afișează; înainte butonul nu arăta nimic.
  async function supprimer() {
    if (!window.confirm('Supprimer ce certificat et ses documents ?')) return;
    setBusy(true); setErr(null);
    try { await api.remove(initial.id); onSaved(); }
    catch (e) { setErr(e?.response?.data?.message || e.message); }
    finally { setBusy(false); }
  }

  async function removeFile(fileId) {
    if (!window.confirm('Supprimer ce document ?')) return;
    setBusy(true);
    try { await api.deleteFile(fileId); setFiles((prev) => prev.filter((f) => f.id !== fileId)); }
    catch (e) { setErr(e?.response?.data?.message || e.message); }
    finally { setBusy(false); }
  }

  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18 }}>{isNew ? 'Nouveau certificat' : (initial.user?.name || 'Certificat')}</h3>
            {!isNew && <p style={{ margin: '2px 0 0', color: '#64748b', fontSize: 12 }}>{fmtFR(form.startDate)} → {fmtFR(form.endDate)}</p>}
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 22, cursor: 'pointer', color: '#64748b' }}>×</button>
        </div>

        {err && <div style={alertErr}>{err}</div>}

        {isNew && (
          <div style={field}>
            <label style={lbl}>Personne</label>
            <select value={form.userId} onChange={(e) => setForm({ ...form, userId: e.target.value })} style={input}>
              <option value="">— Sélectionner —</option>
              {allUsers.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
            </select>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ ...field, flex: 1 }}>
            <label style={lbl}>Du</label>
            <input type="date" value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} style={input} />
          </div>
          <div style={{ ...field, flex: 1 }}>
            <label style={lbl}>Au</label>
            <input type="date" value={form.endDate} onChange={(e) => setForm({ ...form, endDate: e.target.value })} style={input} />
          </div>
        </div>

        <div style={field}>
          <label style={lbl}>Justificatif médical</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {['AVEC', 'SANS', 'EN_ATTENTE'].map((s) => (
              <div key={s} onClick={() => setForm({ ...form, status: s })}
                style={{ border: form.status === s ? '2px solid #4f46e5' : '1px solid #cbd5e1', background: form.status === s ? '#eef2ff' : '#fff', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', fontSize: 13, fontWeight: form.status === s ? 600 : 400 }}>
                <span style={pill(s)}>{STATUS_PILL[s].label}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={field}>
          <label style={lbl}>Note interne (optionnel)</label>
          <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Ex: médecin Dr. X, prolongation possible..." style={input} />
        </div>

        {/* Files — doar după ce certificatul există */}
        {!isNew ? (
          <div style={field}>
            <label style={lbl}>Documents (PDF / JPG / PNG · max 10 Mo)</label>
            <label style={dropzone}>
              📤 Cliquer pour téléverser
              <input type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" onChange={handleUpload} style={{ display: 'none' }} disabled={busy} />
            </label>
            {files.map((f) => (
              <div key={f.id} style={fileChip}>
                <span style={{ background: '#dc2626', color: '#fff', fontSize: 10, fontWeight: 700, padding: '2px 6px', borderRadius: 4 }}>
                  {(f.filename.split('.').pop() || '?').toUpperCase()}
                </span>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.filename}</span>
                <a href={api.fileUrl(f.id, true)} target="_blank" rel="noreferrer" style={{ color: '#4f46e5', fontWeight: 600, fontSize: 12 }}>👁 Aperçu</a>
                <button onClick={() => removeFile(f.id)} style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 14 }}>🗑</button>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ ...field, fontSize: 12, color: '#94a3b8' }}>💡 Enregistrez d'abord pour pouvoir ajouter des documents.</div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
          {!isNew ? (
            <button onClick={supprimer} disabled={busy}
              style={{ ...btnGhost, color: '#dc2626', borderColor: '#fca5a5' }}>Supprimer</button>
          ) : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={btnGhost}>Fermer</button>
            <button onClick={save} disabled={busy} style={btnPrimary}>{busy ? '...' : (isNew ? 'Enregistrer' : 'Mettre à jour')}</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Styles
const selectStyle = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, background: '#fff' };
const btnPrimary = { padding: '7px 14px', borderRadius: 6, border: '1px solid #4f46e5', background: '#4f46e5', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnGhost = { padding: '7px 14px', borderRadius: 6, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const th = { textAlign: 'left', padding: '9px 10px', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#64748b', borderBottom: '2px solid #e2e8f0' };
const td = { padding: 10 };
const alertErr = { padding: 10, background: '#fee2e2', color: '#991b1b', borderRadius: 6, fontSize: 13, marginBottom: 12, border: '1px solid #fca5a5' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 };
const modal = { background: '#fff', borderRadius: 12, padding: 22, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' };
const field = { marginBottom: 14 };
const lbl = { display: 'block', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 5 };
const input = { width: '100%', padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, fontFamily: 'inherit' };
const dropzone = { display: 'block', border: '2px dashed #cbd5e1', borderRadius: 10, padding: 18, textAlign: 'center', color: '#64748b', fontSize: 13, background: '#f8fafc', cursor: 'pointer' };
const fileChip = { display: 'flex', alignItems: 'center', gap: 10, background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8, padding: '8px 12px', marginTop: 8, fontSize: 13 };
